import type {
  BacklogListImport,
  AddressCandidate,
} from "../../../_shared/_logic/batch-upload-contract"
import {
  exactBacklogUnitIds,
  soleBuildingUnitId,
  backlogAddressUnitId,
} from "../_shared/_logic/unit-selection"
import type { LoadListRowDraft } from "./loadListState.svelte"
import { backlogMatchReview } from "../_shared/_logic/row-hints"

type SourceRow = BacklogListImport["rows"][number]

export const workWarningText = (
  warning: NonNullable<SourceRow["work_warnings"]>[number],
): string => {
  const detail = [warning.column, warning.value].filter(Boolean).join(": ")
  const suffix = detail ? ` (${detail})` : ""
  switch (warning.code) {
    case "unknown_code":
      return `Unbekanntes Arbeitskürzel${suffix}. Bitte Auftragstyp prüfen.`
    case "unknown_value":
      return `Arbeitsangabe unklar${suffix}. Bitte Auftragstyp prüfen.`
    case "unmapped_work":
      return `Offene Sonderarbeit${suffix}. Bitte Auftragstyp manuell ergänzen.`
    case "conflicting_work":
      return `Widersprüchliche Arbeitsangaben${suffix}. Bitte in der Quelle prüfen.`
    case "missing_work_columns":
      return "Keine unterstützten Arbeitsspalten vorhanden. Bitte Auftragstypen manuell wählen."
    case "unsupported_layout":
      return "Die anlagenbezogene Liste unterstützt noch keine Arbeitsartenerkennung. Bitte Auftragstypen manuell wählen."
    case "no_open_work":
      return "Keine offenen Arbeiten erkannt. Bitte Auftragstypen prüfen."
  }
}

export type BacklogWorkSuggestion = {
  sources: SourceRow[]
  sourceRowIds: string[]
  ids: string[]
  warnings: string[]
  evidence: string[]
  durationMinutes?: number
}

/** Index sources once by concrete target; unresolved source rows keep their own identity. */
export const backlogWorkSuggestions = (
  backlog: BacklogListImport | null,
  candidates: AddressCandidate[],
  rows: LoadListRowDraft[],
  properties: Property[],
): Map<string, BacklogWorkSuggestion> => {
  const result = new Map<string, BacklogWorkSuggestion>()

  if (!backlog) return result

  const propertyById = new Map(properties.map((property) => [property.id, property]))
  const unitIdsByProperty = new Map(
    properties.map((property) => [
      property.id,
      new Set(property.units?.map((unit) => unit.id) ?? []),
    ]),
  )
  const bySource = new Map(backlog.rows.map((row) => [row.source_row_id, row]))
  const byTarget = new Map<string, SourceRow[]>()
  const unassigned = new Map<string, SourceRow[]>()
  const append = (index: Map<string, SourceRow[]>, key: string, source: SourceRow) => {
    const group = index.get(key)

    if (group) group.push(source)
    else index.set(key, [source])
  }
  for (const source of backlog.rows) {
    if (source.status !== "matched" || !source.property_id) continue

    const property = propertyById.get(source.property_id)

    if (!property) continue

    const unitId =
      source.property_unit_id ??
      (backlog.sourceSystem === "portal_uno_office"
        ? (exactBacklogUnitIds(property, source.fields.customer_number)[0] ??
          soleBuildingUnitId(property))
        : backlogAddressUnitId(property, backlog.sourceSystem))

    if (backlog.sourceSystem === "isi_export") append(unassigned, property.id, source)

    if (unitId && unitIdsByProperty.get(property.id)?.has(unitId))
      append(byTarget, `${property.id}:${unitId}`, source)
    else if (backlog.sourceSystem !== "isi_export") append(unassigned, property.id, source)
  }
  const candidateByKey = new Map(candidates.map((candidate) => [candidate.key, candidate]))
  for (const row of rows) {
    if (row.copyOriginRowId || row.workSourceCopied) continue

    const sourceId = candidateByKey.get(row.sourceCandidateKey)?.backlogResolution?.sourceRowId
    const candidateSource = sourceId ? bySource.get(sourceId) : null
    const ownSource =
      candidateSource &&
      (!candidateSource.property_id || candidateSource.property_id === row.propertyId)
        ? candidateSource
        : null
    const sources = ownSource
      ? [ownSource]
      : (byTarget.get(`${row.propertyId}:${row.selectedUnitId}`) ??
        (row.workTargetSelectedManually && row.propertyId ? unassigned.get(row.propertyId) : null))

    if (!sources?.length) {
      if (candidateByKey.get(row.sourceCandidateKey)?.sources?.includes("backlog_pdf"))
        result.set(row.id, {
          sourceRowIds: [],
          sources: [],
          ids: [],
          warnings: [
            "Keine Arbeitsangaben für diese Einheit zugeordnet. Bitte Auftragstypen prüfen.",
          ],
          evidence: [],
        })

      continue
    }

    const suggestion = {
      sources,
      sourceRowIds: sources.map((source) => source.source_row_id),
      ids: [...new Set(sources.flatMap((source) => source.suggested_order_type_ids ?? []))],
      durationMinutes:
        backlog.options?.mode === "manual" && backlog.options.contract_version === 1
          ? backlog.options.duration_minutes
          : undefined,
      warnings: [
        ...new Set(
          sources.flatMap((source) => [
            ...(source.work_warnings?.map(workWarningText) ?? [
              "Keine Arbeitsartenerkennung verfügbar. Bitte Auftragstypen prüfen.",
            ]),
            ...(source.source_uncertain_columns ?? []).map(
              (index) =>
                `Formatierung unklar in „${backlog.sourceColumns?.[index] || `Spalte ${index + 1}`}“. Der Originaltext ist in den Zeilendetails sichtbar.`,
            ),
          ]),
        ),
      ],
      evidence: [
        ...new Set(
          sources.flatMap((source) =>
            (source.work_evidence ?? [])
              .filter((item) => item.status === "open" && item.order_type_key)
              .map((item) => `${item.column}: ${item.value}`),
          ),
        ),
      ],
    }

    if (!suggestion.ids.length && !suggestion.warnings.length)
      suggestion.warnings.push(
        "Keine offenen Arbeiten erkannt. Bitte Auftragstypen manuell wählen.",
      )

    for (const source of sources) {
      const review = backlogMatchReview(source, backlog.sourceSystem, row)

      if (review.appliesToSelection)
        suggestion.warnings.push(...review.hints.map((hint) => `${review.location}: ${hint}`))
    }

    result.set(row.id, suggestion)
  }

  return result
}
